import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Gear, TrendUp } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const [goldRate, setGoldRate] = useState('');
  const [silverRate, setSilverRate] = useState('');
  const [loading, setLoading] = useState(false);

  const handleUpdateRate = async (metalType, rate) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-rates`, {
        metal_type: metalType,
        rate_per_gram: parseFloat(rate)
      }, { withCredentials: true });
      
      toast.success(`${metalType === 'gold' ? 'Gold' : 'Silver'} rate updated successfully`);
      if (metalType === 'gold') setGoldRate('');
      else setSilverRate('');
    } catch (error) {
      toast.error('Failed to update market rate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Settings
        </h1>
        <p className="text-[#6B7280] mt-2">Manage market rates and system configuration</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Market Rates */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendUp size={24} className="text-[#C9A227]" weight="duotone" />
            <h3 className="text-lg font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Market Rates
            </h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="gold-rate">Gold Rate (per gram)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="gold-rate"
                  type="number"
                  step="0.01"
                  value={goldRate}
                  onChange={(e) => setGoldRate(e.target.value)}
                  placeholder="Enter gold rate"
                  data-testid="gold-rate-input"
                />
                <Button
                  onClick={() => handleUpdateRate('gold', goldRate)}
                  disabled={!goldRate || loading}
                  className="bg-[#C9A227] hover:bg-[#A9851F]"
                  data-testid="update-gold-rate-button"
                >
                  Update
                </Button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="silver-rate">Silver Rate (per gram)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="silver-rate"
                  type="number"
                  step="0.01"
                  value={silverRate}
                  onChange={(e) => setSilverRate(e.target.value)}
                  placeholder="Enter silver rate"
                  data-testid="silver-rate-input"
                />
                <Button
                  onClick={() => handleUpdateRate('silver', silverRate)}
                  disabled={!silverRate || loading}
                  className="bg-[#C9A227] hover:bg-[#A9851F]"
                  data-testid="update-silver-rate-button"
                >
                  Update
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* System Info */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Gear size={24} className="text-[#C9A227]" weight="duotone" />
            <h3 className="text-lg font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              System Information
            </h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center pb-3 border-b border-[#E5E7EB]">
              <span className="text-[#6B7280]">Version</span>
              <span className="font-semibold text-[#1A1A1A]">1.0.0</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-[#E5E7EB]">
              <span className="text-[#6B7280]">System</span>
              <span className="font-semibold text-[#1A1A1A]">Gold & Silver POS</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Environment</span>
              <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs border border-green-200">
                Production
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
